<template>
    <div class="review-club-overview__main-box">
        <h3 class="review-club-overview__title fwb">{{ review.title }}</h3>
        <div class="mb-1">
            <span class="text-success fs-14">{{ review.member.name }} {{ $t('reviewClub.recomended') }}</span>

            <span v-if="review.isVip" class="review-club-overview__badge review-club-overview__badge--vip text-nowrap">
                {{ $t('reviewClub.vipReview') }}
            </span>

            <span
                v-if="!review.isVip && review.member.isPilulkaEmployee"
                class="review-club-overview__badge review-club-overview__badge--secondary text-nowrap"
            >
                {{ $t('reviewClub.isEmployee') }}
            </span>
        </div>

        <ReviewRatingStars :review="review" />

        <div v-if="review.product.sellingName" class="mb-1">
            <span class="text-gray">{{ `${$t('productDetail.rating.variant')} ${review.product.sellingName}` }}</span>
        </div>

        <p class="review-club-overview__introduction-text">
            {{ review.introductionText }}
        </p>

        <div class="row">
            <div v-if="review.pros" class="col-12" :class="{ 'col-sm-6': review.cons }">
                <ul>
                    <li v-for="(item, i) in review.pros.slice(0, 3)" :key="i" class="d-flex align-items-center mb-2">
                        <img
                            v-lazy-load
                            class="me-2"
                            width="25"
                            alt="plus"
                            src="/nuxt-assets/images/lazy-load-error.svg"
                            data-src="/nuxt-assets/images/icons/plus.svg"
                        />
                        {{ item }}
                    </li>
                </ul>
            </div>
            <div v-if="review.cons" class="col-12" :class="{ 'col-sm-6': review.pros }">
                <ul>
                    <li v-for="item in review.cons.slice(0, 3)" :key="item" class="d-flex align-items-center mb-2">
                        <img
                            v-lazy-load
                            class="me-2"
                            width="25"
                            alt="plus"
                            src="/nuxt-assets/images/lazy-load-error.svg"
                            data-src="/nuxt-assets/images/icons/minus.svg"
                        />
                        {{ item }}
                    </li>
                </ul>
            </div>
        </div>

        <div class="mt-auto">
            <button class="btn btn-outline-primary mb-1 py-1" @click="showReviewDetail(review.id)">
                {{ $t('reviewClub.readAllReview') }}
            </button>
        </div>

        <div class="d-flex align-items-center fs-12 mt-2 position-relative justify-content-center justify-content-md-end ml-auto">
            <div class="product-prev__like-box__tooltip product-prev__like-box__tooltip--hidden like-tooltip-2133"></div>
            <span class="btn btn-text fs-12 rate-user-review" @click="rateComment(review.id, 'like')">
                <img
                    v-lazy-load
                    width="15"
                    height="15"
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    data-src="/nuxt-assets/images/icons/like.svg"
                    :alt="$t('reviewClub.like')"
                />
                {{ $t('reviewClub.like') }} (<span>{{ review.likesCount + like }}</span
                >)
            </span>
            <span class="btn btn-text fs-12 rate-user-review" @click="rateComment(review.id, 'dislike')">
                <img
                    v-lazy-load
                    width="15"
                    height="15"
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    data-src="/nuxt-assets/images/icons/dislike.svg"
                    :alt="$t('reviewClub.unlike')"
                />
                {{ $t('reviewClub.unlike') }}
                (<span>{{ review.dislikesCount + dislike }}</span
                >)
            </span>
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import Review from '~/models/ReviewClub/Review'
import ReviewRatingStars from '~/components/objects/ReviewClub/ReviewRatingStars.vue'
import { useAuthStore } from '~/stores/auth'

export default {
    components: { ReviewRatingStars },

    props: {
        review: {
            type: Object as PropType<Review>,
            default: () => ({} as Review),
        },
    },

    data() {
        return {
            like: 0,
            dislike: 0,
        }
    },

    methods: {
        showReviewDetail(reviewId: number) {
            useNuxtApp().$bus.$emit('showReviewDetail', reviewId)
            useNuxtApp().$bus.$emit('showReviewDetailWeTested', reviewId)
        },

        async rateComment(reviewId: number, isUseful: 'like' | 'dislike'): Promise<void> {
            const authStore = useAuthStore()
            const data = {
                reviewId,
                type: isUseful,
                accessToken: authStore.accessToken,
            }

            if (!data.accessToken) {
                useNuxtApp().$bus.$emit('showModal', `ask-question-login-${reviewId}`)
            } else {
                this.like = isUseful === 'like' ? 1 : 0
                this.dislike = isUseful === 'dislike' ? 1 : 0
            }

            this.loading = true

            try {
                await this.$repository.rateReviewClub(data)

                this.loading = false
            } catch (err) {
                this.loading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '../../../../node_modules/bootstrap/scss/functions';
@import '../../../../node_modules/bootstrap/scss/variables';
@import '../../../../node_modules/bootstrap/scss/mixins';

.review-club-overview {
    ul {
        padding-left: 0;

        li {
            font-size: 13px;
        }

        img {
            width: 17px;
            height: 17px;
        }
    }

    &__main-box {
        width: calc(100% - 92px);
        padding: 1.5em 1em 1em;
        display: flex;
        flex-direction: column;
    }

    &__introduction-text {
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    &-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1em;
    }

    &__title {
        font-size: 16px;
        height: 38px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        margin: 0 0 5px;
    }

    &__badge {
        font-size: 12px;
        border-radius: 12px;
        padding: 3px 10px;
        margin-left: 5px;

        &--primary {
            color: $color-cta;
            background-color: #fae5f1;
        }

        &--secondary {
            color: $color-green-dark;
            background-color: $color-green-light;
        }

        &--vip {
            color: $color-purple-dark;
            border: 1px solid $color-purple-dark;
        }
    }
}
</style>
